import { Component } from '@theme/component';
import { prefersReducedMotion, isLowPowerDevice } from '@theme/utilities';

const finePointer = window.matchMedia('(any-pointer: fine)');

/** @type {Set<MagnetComponent>} */
const magnets = new Set();
let pointerX = 0;
let pointerY = 0;
/** @type {number | null} */
let rafId = null;
let listening = false;

/**
 * Single document-level pointermove listener shared by every magnet instance.
 * @param {PointerEvent} event
 */
function onPointerMove(event) {
  pointerX = event.clientX;
  pointerY = event.clientY;
  if (rafId === null) rafId = requestAnimationFrame(update);
}

function update() {
  rafId = null;
  let active = false;

  for (const magnet of magnets) {
    if (magnet.update(pointerX, pointerY)) active = true;
  }

  // Keep animating (spring back) even when the pointer stops emitting events.
  if (active) rafId = requestAnimationFrame(update);
}

/** @param {MagnetComponent} magnet */
function register(magnet) {
  magnets.add(magnet);

  if (!listening) {
    document.addEventListener('pointermove', onPointerMove, { passive: true });
    listening = true;
  }
}

/** @param {MagnetComponent} magnet */
function unregister(magnet) {
  magnets.delete(magnet);

  if (magnets.size === 0 && listening) {
    document.removeEventListener('pointermove', onPointerMove);
    listening = false;

    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }
}

/**
 * A custom element that attracts its content toward the pointer while it hovers
 * nearby, springing back to rest when the pointer leaves.
 *
 * @example
 * <magnet-component data-magnet="0.3">
 *   <a href="...">...</a>
 * </magnet-component>
 */
class MagnetComponent extends Component {
  /** Current translate offset. */
  #x = 0;
  #y = 0;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion() || isLowPowerDevice() || !finePointer.matches) return;

    register(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unregister(this);
    this.style.removeProperty('translate');
    this.#x = 0;
    this.#y = 0;
  }

  /**
   * Advance one animation frame.
   * @param {number} px - pointer x in viewport coordinates
   * @param {number} py - pointer y in viewport coordinates
   * @returns {boolean} whether this magnet still needs animation frames
   */
  update(px, py) {
    const strength = Number(this.dataset.magnet) || 0.3;
    const rect = this.getBoundingClientRect();

    // Untransformed center: subtract the current translate offset.
    const cx = rect.left + rect.width / 2 - this.#x;
    const cy = rect.top + rect.height / 2 - this.#y;
    const radius = Math.max(rect.width, rect.height) / 2 + 32;

    const dx = px - cx;
    const dy = py - cy;
    const within = Math.hypot(dx, dy) < radius;

    const targetX = within ? dx * strength : 0;
    const targetY = within ? dy * strength : 0;

    this.#x += (targetX - this.#x) * 0.18;
    this.#y += (targetY - this.#y) * 0.18;

    if (!within && Math.abs(this.#x) < 0.05 && Math.abs(this.#y) < 0.05) {
      if (this.#x !== 0 || this.#y !== 0) {
        this.#x = 0;
        this.#y = 0;
        this.style.removeProperty('translate');
      }
      return false;
    }

    this.style.translate = `${this.#x}px ${this.#y}px`;
    return true;
  }
}

if (!customElements.get('magnet-component')) {
  customElements.define('magnet-component', MagnetComponent);
}
